import React, { useCallback, useEffect, useState } from "react";
import * as api from "./csaApi";
import "./CsaOffline.css";

/**
 * The offline controls: a persistent sync indicator, and the button that makes
 * an audit available offline (design §9).
 *
 * The design is blunt about what these are for: "the failure mode that destroys
 * trust is an auditor driving to a plant and finding nothing cached. The audit
 * list must show offline-readiness per audit, not bury it." So readiness is
 * stated on the card, and the indicator is always visible while capturing —
 * never a transient toast that an auditor might miss.
 */

/* ------------------------------------------------------------------ */
/* the indicator                                                       */
/* ------------------------------------------------------------------ */

const LABEL = {
  offline: "Offline",
  syncing: "Syncing…",
  pending: "Waiting to sync",
  failing: "Sync problem",
  synced: "All synced",
};

export function CsaSyncBadge({ auditId, onConflicts }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => api.subscribeSync(setStatus), []);

  const loadQueue = useCallback(() => {
    api.pending(auditId).then(setQueue).catch(() => setQueue([]));
  }, [auditId]);

  useEffect(() => {
    if (open) loadQueue();
  }, [open, status, loadQueue]);

  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await api.flush(auditId);
      setConflicts(r.conflicts || []);
      if (onConflicts) onConflicts(r.conflicts || []);
      loadQueue();
    } catch (e) {
      /* the indicator already reflects it */
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  // Nothing to say on a desk session that was never provisioned.
  if (status.state === "synced" && !status.pending && api.isOnline()) {
    return null;
  }

  const count = status.pending;

  return (
    <div className="csa-sync">
      <button
        type="button"
        className={"sync-badge s-" + status.state}
        onClick={() => setOpen(!open)}
        title={LABEL[status.state]}
      >
        <span className="dot" />
        {LABEL[status.state]}
        {count ? <span className="n">{count}</span> : null}
      </button>

      {open ? (
        <div className="sync-pop">
          <h4>{LABEL[status.state]}</h4>

          <p className="sync-explain">
            {status.state === "offline"
              ? "Your work is saved on this device. It will sync by itself when you are back on a network."
              : status.state === "failing"
              ? "Some changes could not be sent. They are still on this device and nothing has been lost."
              : count
              ? `${count} change${count === 1 ? "" : "s"} still to send.`
              : "Everything on this device has reached the server."}
          </p>

          {conflicts.length ? (
            <div className="sync-conflicts">
              <strong>
                {conflicts.length} change{conflicts.length === 1 ? "" : "s"} clashed with
                someone else&apos;s edit
              </strong>
              {conflicts.map((c) => (
                <div className="sync-conflict" key={c.key}>
                  <div className="cf-what">
                    {c.entity} · {c.entry && c.entry.entity_key}
                  </div>
                  <div className="cf-msg">{c.message}</div>
                  <button
                    type="button"
                    className="small-btn warn"
                    onClick={() => api.discard(auditId, c.key).then(loadQueue)}
                  >
                    Discard mine
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {queue.length ? (
            <ul className="sync-queue">
              {queue.slice(0, 8).map((e) => (
                <li key={e.key}>
                  <span>
                    {e.entity} · {e.op}
                  </span>
                  <span className="q-key">{e.entity_key}</span>
                  {e.last_error ? <em>{e.last_error}</em> : null}
                </li>
              ))}
              {queue.length > 8 ? <li className="more">and {queue.length - 8} more</li> : null}
            </ul>
          ) : null}

          <div className="sync-actions">
            <button
              type="button"
              className="small-btn"
              disabled={busy || !status.online || !count}
              onClick={syncNow}
            >
              {busy ? "Syncing…" : "Sync now"}
            </button>
            <button type="button" className="small-btn" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* provisioning                                                        */
/* ------------------------------------------------------------------ */

/**
 * "Make available offline".
 *
 * Explicit rather than automatic, and it reports what it stored. An auditor
 * about to drive to a plant can see the number of questions and answers on the
 * device, which is the thing they need to trust before losing signal.
 */
export function CsaOfflineButton({ auditId, compact, onChange }) {
  const [ready, setReady] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const refresh = useCallback(() => {
    api
      .isProvisioned(auditId)
      .then(setReady)
      .catch(() => setReady(false));
  }, [auditId]);

  useEffect(refresh, [refresh]);

  const download = async () => {
    setError(null);
    setProgress({ label: "Starting", pct: 0 });
    try {
      const r = await api.provision(auditId, setProgress);
      setResult(r);
      setReady(true);
      if (onChange) onChange(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setProgress(null);
    }
  };

  const remove = async () => {
    setError(null);
    try {
      await api.unprovision(auditId);
      setReady(false);
      setResult(null);
      if (onChange) onChange(false);
    } catch (e) {
      // forgetAudit refuses while anything is queued — that refusal is the
      // whole point, so it is shown rather than swallowed.
      setError(e.message);
    }
  };

  if (ready === null) return null;

  if (progress) {
    return (
      <div className="offline-progress">
        <div className="bar">
          <span style={{ width: progress.pct + "%" }} />
        </div>
        <span>{progress.label}…</span>
      </div>
    );
  }

  if (compact) {
    return ready ? (
      <span className="offline-chip ready" title="Saved on this device">
        ✓ On this device
      </span>
    ) : (
      <button
        type="button"
        className="offline-chip"
        onClick={(e) => {
          e.stopPropagation();
          download();
        }}
      >
        Make available offline
      </button>
    );
  }

  /*
   * Dismissal lasts for this visit only, on purpose. The failure this banner
   * guards against — driving to a plant with nothing cached — is bad enough
   * that "I hid it last week" should not carry over to the next time the
   * audit is opened.
   */
  if (dismissed && !ready) return null;

  return (
    <div className={"offline-box" + (ready ? " is-ready" : "")}>
      {error ? <p className="offline-error">{error}</p> : null}

      {ready ? (
        <>
          <div className="offline-ready">
            <strong>Ready for offline</strong>
            <span>
              {result
                ? `${result.questions} questions and ${result.answers} answers are on this device.`
                : "This audit is saved on this device."}
            </span>
            {result && result.taken_over ? (
              <em>
                This audit was open on another device. It has been moved to this one — make
                sure the other device has synced.
              </em>
            ) : null}
          </div>
          <button type="button" className="small-btn warn" onClick={remove}>
            Remove from this device
          </button>
        </>
      ) : (
        <>
          {/*
            One line, not a paragraph.

            This is a single action taken once before leaving the office, and it
            used to hold a 110px strip above every question for the whole audit —
            on a tablet in portrait that is a meaningful part of the screen spent
            on something already read and understood.
          */}
          <div className="offline-ready">
            <strong>Not saved on this device</strong>
            <span>Download it before you travel — offline capture needs it.</span>
          </div>
          <button type="button" className="cap-btn" onClick={download}>
            Make available offline
          </button>
          <button
            type="button"
            className="offline-x"
            title="Hide until next time this audit is opened"
            aria-label="Dismiss"
            onClick={() => setDismissed(true)}
          >
            ✕
          </button>
        </>
      )}
    </div>
  );
}
